import React from 'react';
import { Monitor, Smartphone, RotateCcw, Sun, Moon } from 'lucide-react';
import { useViewMode } from '../contexts/ViewModeContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export default function ViewModeToggle({ className = '' }) {
  const {
    viewMode,
    isMobileView,
    userPreference,
    isManualOverride,
    toggleViewMode,
    setViewModePreference,
    resetToAuto,
  } = useViewMode();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`gap-2 ${className}`}
          data-testid="view-mode-toggle"
        >
          {isMobileView ? (
            <Smartphone className="w-4 h-4" />
          ) : (
            <Monitor className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {isMobileView ? 'Mobile' : 'Desktop'}
          </span>
          {isManualOverride && (
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" title="Manual override active" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-xs text-neutral-500 font-normal">
          View Mode
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={() => setViewModePreference('desktop')}
          className="gap-2 cursor-pointer"
          data-testid="view-mode-desktop"
        >
          <Monitor className="w-4 h-4" />
          <span>Desktop View</span>
          {userPreference === 'desktop' && (
            <span className="ml-auto text-pink-500">✓</span>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          onClick={() => setViewModePreference('mobile')}
          className="gap-2 cursor-pointer"
          data-testid="view-mode-mobile"
        >
          <Smartphone className="w-4 h-4" />
          <span>Mobile View</span>
          {userPreference === 'mobile' && (
            <span className="ml-auto text-pink-500">✓</span>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={resetToAuto}
          className="gap-2 cursor-pointer"
          data-testid="view-mode-auto"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Auto-detect</span>
          {userPreference === 'auto' && (
            <span className="ml-auto text-pink-500">✓</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Simple toggle button variant (for compact spaces)
export function ViewModeToggleSimple({ className = '' }) {
  const { isMobileView, toggleViewMode, isManualOverride } = useViewMode();
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="flex items-center gap-1.5">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className={`relative p-2 rounded-xl border transition-colors ${
          isDark 
            ? 'border-white/20 bg-white/10 hover:bg-white/20' 
            : 'border-white/40 hover:bg-white/60'
        }`}
        title={`Switch to ${isDark ? 'Light' : 'Dark'} mode`}
        data-testid="theme-toggle"
      >
        {isDark ? (
          <Sun className="w-5 h-5 text-amber-400" />
        ) : (
          <Moon className="w-5 h-5 text-neutral-600" />
        )}
      </button>
      {/* View Mode Toggle */}
      <button
        onClick={toggleViewMode}
        className={`relative p-2 rounded-xl border transition-colors ${
          isDark 
            ? 'border-white/20 bg-white/10 hover:bg-white/20' 
            : 'border-white/40 hover:bg-white/60'
        }`}
        title={`Switch to ${isMobileView ? 'Desktop' : 'Mobile'} view`}
        data-testid="view-mode-toggle-simple"
      >
        {isMobileView ? (
          <Smartphone className={`w-5 h-5 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`} />
        ) : (
          <Monitor className={`w-5 h-5 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`} />
        )}
        {isManualOverride && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-pink-500" />
        )}
      </button>
    </div>
  );
}
